//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { useState } from 'react';
import { Instance } from 'tippy.js';

type ConnectPopover = (instance: Instance) => void;

interface IPopoverMonitor {
  closePopover: () => void;
  openPopover: () => void;
  state: Instance['state'] | undefined;
}

const fallbackMethod = () => undefined;

/**
 * This hook is a layer on Tippy's Instance functionality that allows
 * consumers of the Tippy Popover to easily connect an instance into the hook
 * and have access to force open/close methods outside of the normal click or
 * hover behavior. The return values of this hook are as follows:
 *
 * 1. The Popover connector function, which should be passed into the Popover
 * component as a prop to access this hook's functionality.
 * 2. An object of methods/instances that are accessed off of the Popover instance,
 * such as opening and closing the popover.
 */
export const usePopover = (): [ConnectPopover, IPopoverMonitor] => {
  const [popoverInstance, connectPopover] = useState<Instance | null>(null);

  return [
    connectPopover,
    {
      openPopover: popoverInstance?.show ?? fallbackMethod,
      closePopover: popoverInstance?.hide ?? fallbackMethod,
      state: popoverInstance?.state,
    } as const,
  ];
};
